/* @flow */
/**
 * Redux Toolkit slice for SIRE delivery profile search, downloads, metadata, and search form state.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import { initialSireSearchFields } from '../../constants/staticData';

export const name = 'sireDelivery';

function createInitialState() {
  return {
    initialSireFieldsState: { ...initialSireSearchFields },
    sireProfileSummaryData: [],
    countryList: [],
    sireProfileSearchId: '',
    noResults: false
  };
}

export const initialState = createInitialState();

/**
 * Maps profile IDs into the request shape expected by SIRE download APIs.
 */
export const getSearchId = (ids: Object, profielSearchId: Object) => {
  ids.forEach((id) => profielSearchId.push({
    profileId: id
  }));
};

/**
 * Maps SIRE search form fields to API search field names.
 */
const mapSireDeliveryRequest = (row) => ({
  customerShipTo: row.shipToID,
  titleTransferModelCode: row.ttmi,
  divisionCode: row.division,
  buyGroupCode: row.buyGroup,
  transportationModeCode: row.mode,
  manufacturingCountryOfOrigin: row['originCountry.code'],
  destinationCountryCode: row['destCountry.code'],
  plantCode: row.plantID
});

/**
 * Detects values that should be omitted from SIRE API requests.
 */
function isEmptyFieldValue(value: any) {
  if (value === '' || value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string' || Array.isArray(value)) {
    // Empty strings and arrays should not be sent as search criteria.
    return value.length === 0;
  }
  return false;
}

/**
 * Returns a copy of an object without empty-valued request fields.
 */
function deleteEmptyKeysFromObject(fieldValue: { [string]: any }): { [string]: any } {
  return Object.fromEntries(
    // Keep falsy-but-valid values while dropping only empty request fields.
    Object.entries(fieldValue).filter(([, entryValue]) => !isEmptyFieldValue(entryValue))
  );
}

/**
 * Removes empty fields from a SIRE search request payload.
 */
export const removeEmptyValueFromRequest = (inputField: any) => (
  deleteEmptyKeysFromObject(inputField)
);

/**
 * Maps selected SIRE profile rows to the download request shape.
 */
const mapSireDeliveryProfileRequest = (data) => data.map((item) => ({
  profileId: item.profileID,
  shipToId: item.shipToID,
  plantId: item.plantID,
  ttmi: item.ttmi,
  division: item.division,
  buyGroup: item.buyGroup,
  mode: item.mode,
  originCountry: item.originCountry,
  destCountry: item['destCountry.code']
}));

/**
 * Removes blank values from each SIRE profile download request item.
 */
const removeBlankValues = (data) => data.map((item) => deleteEmptyKeysFromObject(item));

/**
 * Handles SIRE request failures by stopping the spinner and notifying callbacks.
 */
const catchError = (error, dispatch, callback) => {
  dispatch(toggleSpinner(false));
  if (callback) {
    callback(null, error);
  }
};

/**
 * Stores the current SIRE search form values.
 */
function updateSireSearchInitialField(state: Object, action: Object) {
  return {
    ...state,
    initialSireFieldsState: action.payload
  };
}

/**
 * Resets SIRE delivery state to its initial values.
 */
function resetSireSearchField(state: Object) {
  return { ...state, ...initialState };
}

/**
 * Clears SIRE search results while keeping supplied search form values.
 */
function clearSireSearchFieldValue(state: Object, action: Object) {
  return {
    ...state,
    initialSireFieldsState: action.payload,
    sireProfileSummaryData: [],
    noResults: false
  };
}

/**
 * Stores SIRE profile search results and updates the no-results flag.
 */
function updateSireProfileSearchData(state: Object, action: Object) {
  // Flattened responses expose profile rows through the objects collection.
  const objects = action.payload?.objects ?? [];
  return {
    ...state,
    sireProfileSummaryData: objects,
    sireProfileSearchId: cryptoRand().toString().slice(2, 10),
    noResults: objects.length === 0
  };
}

/**
 * Stores country dropdown options for SIRE search.
 */
function setCountryCodeDropdown(state: Object, action: Object) {
  return {
    ...state,
    countryList: action.payload
  };
}

/**
 * Collects reducers that manage SIRE delivery state.
 */
function createReducers() {
  return {
    updateSireSearchInitialField,
    resetSireSearchField,
    clearSireSearchFieldValue,
    updateSireProfileSearchData,
    setCountryCodeDropdown
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Formats metadata country records for SIRE dropdown options.
 */
function mapCountryCodeOptions(countryList) {
  return Array.isArray(countryList)
    ? countryList.map((option) => ({
      value: option.countryCode,
      text: `${option.countryCode} | ${option.countryName}`
    }))
    : [];
}

/**
 * Creates a thunk that exports SIRE delivery instructions through SireDeliveryInstructions.
 */
function fetchProfileSireInstructions() {
  return createAsyncThunk(
    `${name}/fetchProfileSireInstructions`,
    async (
      {
        fetchProfileSireInstructionsCallback, ids, filetype, supressSpinner
      },
      { dispatch }
    ) => {
      if (!supressSpinner) {
        dispatch(toggleSpinner(true));
      }
      const profielSearchId = [];
      getSearchId(ids, profielSearchId);
      const requestBody = {
        profileSearch: profielSearchId,
        fileFormat: filetype
      };
      request(
        {
          api: 'SireDeliveryInstructions',
          method: 'post',
          data: requestBody,
          cancellable: true
        },
        dispatch
      )
        .then((response) => {
          if (fetchProfileSireInstructionsCallback) {
            fetchProfileSireInstructionsCallback(response.data);
          }
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          if (fetchProfileSireInstructionsCallback) {
            fetchProfileSireInstructionsCallback(null, error);
          }
          dispatch(toggleSpinner(false));
        });
    }
  );
}

/**
 * Creates a thunk that downloads a blob from a provided URL.
 */
function fetchBlobRequest() {
  return createAsyncThunk(
    `${name}/fetchBlobRequest`,
    async ({ blobUrl, callback }, { getState, dispatch }) => {
      request(
        {
          url: blobUrl,
          method: 'get',
          responseType: 'blob'
        },
        dispatch,
        getState
      )
        .then((response) => {
          if (callback) {
            callback(response.data);
          }
        })
        .catch((error) => {
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that searches SIRE profiles through SIREPROFILESEARCH.
 */
function fetchSireDeliveryProfile() {
  return createAsyncThunk(
    `${name}/fetchSireDeliveryProfile`,
    async ({ callback, data }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const modifiedData = JSON.parse(JSON.stringify(data));
      // Empty TTMI searches are represented as an array containing an empty string.
      if (data.ttmi === '') {
        modifiedData.ttmi = [''];
      // The NoTTMI option maps to a blank TTMI value expected by the API.
      } else if (data.ttmi === 'NoTTMI') {
        modifiedData.ttmi = '';
      } else {
        modifiedData.ttmi = data?.ttmi?.split(',');
      }
      const requestBody = {
        search: removeEmptyValueFromRequest(
          mapSireDeliveryRequest(modifiedData)
        )
      };
      dispatch(actions.updateSireSearchInitialField(data));
      request(
        {
          api: 'SIREPROFILESEARCH',
          method: 'post',
          data: requestBody
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(
            actions.updateSireProfileSearchData(
              getflattenResponse(response.data ?? {})
            )
          );
          if (callback) {
            callback(response);
          }
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          catchError(error, dispatch, callback);
        });
    }
  );
}

/**
 * Creates a thunk that downloads selected SIRE profiles through DOWNLOADSIREPROFILE.
 */
function downloadSireProfile() {
  return createAsyncThunk(
    `${name}/downloadSireProfile`,
    async (
      { downloadSireCallback, dowloadData, filetype },
      { getState, dispatch }
    ) => {
      dispatch(toggleSpinner(true));
      const requestBody = {
        eventType: 'PROFILE_ID_SEARCH',
        eventRequest: removeBlankValues(
          mapSireDeliveryProfileRequest(dowloadData)
        ),
        fileFormat: filetype
      };
      request(
        {
          api: 'DOWNLOADSIREPROFILE',
          method: 'post',
          data: requestBody
        },
        dispatch,
        getState
      )
        .then((response) => {
          if (downloadSireCallback) {
            downloadSireCallback(response.data);
          }
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          if (downloadSireCallback) {
            downloadSireCallback(null, error);
          }
          dispatch(toggleSpinner(false));
        });
    }
  );
}

/**
 * Creates a thunk that loads SIRE metadata for search panel dropdowns.
 */
function getsearchPanelData() {
  return createAsyncThunk(
    `${name}/getsearchPanelData`,
    async ({ callback }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const state = getState();
      // Reuse metadata already in state instead of refetching dropdown values.
      if (state.sireDelivery?.countryList?.length > 0) {
        dispatch(toggleSpinner(false));
        return false;
      }
      request(
        {
          api: 'METADATA',
          method: 'get'
        },
        dispatch,
        getState
      )
        .then((response) => {
          const { countryList } = response.data ?? {};
          dispatch(actions.setCountryCodeDropdown(mapCountryCodeOptions(countryList)));
          if (callback) {
            callback(response.data);
          }
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          catchError(error, dispatch, callback);
        });
      return true;
    }
  );
}

/**
 * Instantiates async SIRE delivery actions exposed by the slice.
 */
function createExtraActions() {
  return {
    fetchProfileSireInstructions: fetchProfileSireInstructions(),
    fetchBlobRequest: fetchBlobRequest(),
    fetchSireDeliveryProfile: fetchSireDeliveryProfile(),
    downloadSireProfile: downloadSireProfile(),
    getsearchPanelData: getsearchPanelData()
  };
}

export const extraActions = createExtraActions();

export const sireDeliveryActions = { ...actions, ...extraActions };
export const sireDeliveryReducer = slice.reducer;
